export const MAX_EQUATION_LENGTH = 4000;
export const EQUATION_DISPLAYS = new Set(['inline', 'block']);

const BASE64URL_RE = /^[A-Za-z0-9_-]+$/;
const DISALLOWED_CONTROL_RE =
  /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/u;

const encodeUtf8 = (value) => {
  if (typeof window.TextEncoder === 'function') {
    return new window.TextEncoder().encode(value);
  }

  const encoded = unescape(encodeURIComponent(value));
  return Uint8Array.from(
    encoded,
    (character) => character.charCodeAt(0)
  );
};

const decodeUtf8 = (bytes) => {
  if (typeof window.TextDecoder === 'function') {
    return new window.TextDecoder('utf-8', { fatal: true }).decode(bytes);
  }

  const escaped = Array.from(bytes, (byte) =>
    `%${byte.toString(16).padStart(2, '0')}`
  ).join('');
  return decodeURIComponent(escaped);
};

export const encodeEquationLatex = (latex) => {
  const bytes = encodeUtf8(String(latex));
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

export const decodeEquationLatex = (encoded) => {
  if (typeof encoded !== 'string' || !BASE64URL_RE.test(encoded)) {
    throw new Error('La source Base64URL de l’équation est invalide.');
  }

  const padding = '='.repeat((4 - (encoded.length % 4)) % 4);
  let binary;
  try {
    binary = atob(
      encoded.replace(/-/g, '+').replace(/_/g, '/') + padding
    );
  } catch {
    throw new Error('La source Base64URL de l’équation est invalide.');
  }

  const bytes = Uint8Array.from(
    binary,
    (character) => character.charCodeAt(0)
  );
  try {
    return decodeUtf8(bytes);
  } catch {
    throw new Error('La source Base64URL de l’équation est invalide.');
  }
};

export const normalizeBookEquation = ({ latex, display } = {}) => {
  const source = typeof latex === 'string' ? latex : '';
  if (!source.trim()) {
    throw new Error('Une équation ne peut pas être vide.');
  }
  if (source.length > MAX_EQUATION_LENGTH) {
    throw new Error('Une équation est limitée à 4 000 caractères.');
  }
  if (DISALLOWED_CONTROL_RE.test(source)) {
    throw new Error('La source contient un caractère de contrôle interdit.');
  }
  if (!EQUATION_DISPLAYS.has(display)) {
    throw new Error('Le mode d’équation est invalide.');
  }

  return {
    latex: source,
    display,
    encoded: encodeEquationLatex(source),
  };
};

export const equationAnchorAttributes = (equation) => {
  const normalized = normalizeBookEquation(equation);
  return {
    'data-book-equation': 'true',
    'data-equation-display': normalized.display,
    'data-equation-latex': normalized.encoded,
  };
};

export const equationAnchorHtml = (equation) => {
  const normalized = normalizeBookEquation(equation);
  const tag = normalized.display === 'inline' ? 'span' : 'div';
  return `<${tag} data-book-equation="true" data-equation-display="${normalized.display}" data-equation-latex="${normalized.encoded}"></${tag}>`;
};
